/**
 * 이상한 방 - 매치 세션의 두 방 (`rooms` 메시지 → 씬에 올라간 방)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 2절(스냅샷 두 벌) · 11.2절(클라이언트가 가진 것 = 두 방과 뷰)
 *            로드맵 모델 2.3절(방 데이터는 뷰가 아니라 별도 `rooms` 메시지)
 *
 * `matchSession.ts`에서 갈라져 나왔다. 클라이언트가 가진 방은 둘이고(외우기에 `original`, 수색에 `mutated`), 그 둘을
 * `decodeRoom`으로 복원해 들고 **지금 씬에 어느 것이 올라가 있는가**를 기억하는 것이 이 파일의 전부다. 싱글의
 * `renderRoom`·`showRoom`·`expectedRoom`(`session.ts`)에 해당하는 자리이고 규칙은 없다 - 무엇을 언제 보일지는
 * 효과(`warpTo`·전환)가 정하고 여기는 그 요청을 `syncObjectViews`로 옮길 뿐이다.
 *
 * ## 복원한 방은 호스트의 것과 참조가 다르다
 *
 * 루프백에서도 방은 코덱을 지나 온다(`loopbackTransport.ts` 머리말). 그래서 이 파일이 든 `Map`은 호스트 상태의 `Map`이
 * 아니고, 실수로 건드려도 정답표가 흔들리지 않는다. 대신 **같은 방이 다시 오면 참조가 새것이다** - `renderRoom`의
 * 참조 비교가 그때 diff를 한 번 더 돌리는데, 안 바뀐 오브젝트의 뷰는 그대로라 비용은 없다.
 *
 * ## 첫 방은 즉시 올린다
 *
 * 외우기 진입의 `warpTo{false}`는 디졸브로 "직전 방 → 원본"을 감추는데, 첫 라운드에는 직전 방이 없다(빈 씬). 감출 것이
 * 없는데 0.4초를 먹색으로 두면 첫 화면이 검다. 그래서 방을 처음 받는 순간에는 바로 올리고, 그 뒤부터는 효과에 맡긴다.
 */

import { addObject, createRoom, removeObject, type Catalog } from '../core/room'
import { decodeRoom } from '../core/roomCodec'
import type { InstanceId, ObjectInstance, RoomState } from '../core/types'
import type { RoomsPayload } from '../net/protocol'
import { syncObjectViews } from '../render/objectView'
import type { SceneCtx } from '../render/scene'

/** 클라이언트가 받은 두 방(11.2절). 페이즈 경계의 `rooms` 메시지로 채워진다. `null`은 아직 안 왔거나 보여 줄 수 없는 것. */
export type MatchRooms = Readonly<{ original: RoomState | null; mutated: RoomState | null }>

export type MatchScene = Readonly<{
  rooms: () => MatchRooms
  /**
   * 물건 하나. 변경본을 먼저 본다(추가·이동은 거기 있다) - 제거된 것은 원본에만 있다. 두 방 어디에도 없으면(스냅샷이 아직 안 왔다)
   * `undefined`. 남의 발견 자리(이름표 · 관전 카메라)를 잡는 두 배선이 같은 조회를 쓴다 - 한쪽만 원본을 먼저 보면 이동된 물건의
   * 이름표와 카메라가 다른 자리를 가리킨다.
   */
  objectOf: (id: InstanceId) => ObjectInstance | undefined
  /** `rooms`·`welcome`의 방 데이터. 복원해 들고, 첫 방이면 즉시 올린다(머리말). */
  receive: (payload: RoomsPayload) => void
  /** 지금 화면에 있어야 할 방. 변경본이 아직 없으면(외우기) 원본이다. 둘 다 없으면 `null`. */
  expectedRoom: (toOrigin: boolean) => RoomState | null
  /** 요청한 방을 씬에 올린다. 같은 참조면 건너뛴다(diff 동기화 - `objectView.syncObjectViews`). */
  showRoom: (toOrigin: boolean) => void
  /**
   * 지금 그려져 있는 방(배치 모드가 이어받는다). 씬을 믿을 수 없을 때는 있어야 할 방이고, 방이 아직 안 왔으면 빈 방이다 -
   * 에디터가 첫 프레임에 이어받을 것이 없다는 뜻이고, 그것이 사실이다.
   */
  visibleRoom: (toOrigin: boolean) => RoomState
  /**
   * 사람 체인저의 초안(M4). 도구가 `core/verbs`로 낙관적으로 그린 방을 변경본 자리에 들고 곧바로 올린다 - 바꾸기 중에는
   * 호스트가 `rooms`를 다시 보내지 않으므로(`matchChange.ts` 머리말) 이 방의 출처는 도구 하나다. 수색 진입의 `rooms`가
   * 호스트의 변경본(초안 + 채움)으로 덮는다.
   */
  showDraft: (room: RoomState) => void
  /**
   * 꾸미기 중 방이 **한 물건씩** 자란다(M10). 호스트가 `placed`/`unplaced` 효과로 한 건씩 알리므로
   * (`matchDecorate.ts` 머리말 - 60초 동안 방을 통째로 다시 보낼 수는 없다) 여기서 그 한 건을 얹고 곧바로 올린다.
   * 꾸미기의 두 방은 같은 것이라 `original`만 만진다 - 뷰가 `mutated`를 `null`로 내린다(`matchView.roomsFor`).
   * 방이 아직 안 왔으면 아무 일도 없다(첫 `rooms`가 곧 온다 - 그것이 진실이고 이 한 건은 그 안에 이미 있다).
   */
  decorate: (change: Readonly<{ placed: ObjectInstance } | { unplaced: InstanceId }>) => void
  /** 씬에 대한 신뢰를 끊는다. 배치 모드가 씬을 편집했으니 다시 들어올 때 무조건 다시 그린다. */
  forget: () => void
}>

export function createMatchScene(ctx: SceneCtx, catalog: Catalog, templateId: string): MatchScene {
  let rooms: MatchRooms = { original: null, mutated: null }
  /** 지금 씬에 올라가 있는 방. `null`이면 씬을 믿을 수 없다. */
  let renderedRoom: RoomState | null = null

  function renderRoom(room: RoomState): void {
    if (renderedRoom === room) return
    syncObjectViews(ctx, room, catalog)
    renderedRoom = room
  }

  function expectedRoom(toOrigin: boolean): RoomState | null {
    if (toOrigin) return rooms.original
    return rooms.mutated ?? rooms.original
  }

  function showRoom(toOrigin: boolean): void {
    const room = expectedRoom(toOrigin)
    if (room !== null) renderRoom(room)
  }

  return {
    rooms: () => rooms,
    objectOf: (id) => rooms.mutated?.objects.get(id) ?? rooms.original?.objects.get(id),

    receive(payload: RoomsPayload): void {
      const hadRoom = rooms.original !== null
      rooms = {
        original: payload.original === null ? null : decodeRoom(payload.original),
        mutated: payload.mutated === null ? null : decodeRoom(payload.mutated),
      }
      if (!hadRoom && rooms.original !== null) showRoom(false)
    },

    expectedRoom,
    showRoom,

    showDraft(room: RoomState): void {
      rooms = { ...rooms, mutated: room }
      renderRoom(room)
    },

    decorate(change): void {
      const before = rooms.original
      if (before === null) return
      const after = 'placed' in change ? addObject(before, change.placed) : removeObject(before, change.unplaced)
      rooms = { original: after, mutated: null }
      renderRoom(after)
    },
    visibleRoom: (toOrigin) => renderedRoom ?? expectedRoom(toOrigin) ?? createRoom(templateId),

    forget(): void {
      renderedRoom = null
    },
  }
}
